
import { useState } from 'react';

export default function ProductTabs() {
  const [activeTab, setActiveTab] = useState<'desc' | 'spec' | 'review'>('desc');

  const specs = [
    { label: '모델명', value: 'MZ-V9P2T0BW' },
    { label: '시리즈', value: '990 PRO' },
    { label: '용량', value: '2TB' },
    { label: '인터페이스', value: 'PCIe Gen 4.0 x4, NVMe 2.0' },
    { label: '폼팩터', value: 'M.2 (2280)' },
    { label: '순차 읽기', value: '최대 7,450 MB/s' },
    { label: '순차 쓰기', value: '최대 6,900 MB/s' },
    { label: '랜덤 읽기 (QD32)', value: '최대 1,400K IOPS' },
    { label: '랜덤 쓰기 (QD32)', value: '최대 1,550K IOPS' },
    { label: 'NAND 타입', value: 'Samsung V-NAND 3-bit MLC (TLC)' },
    { label: '컨트롤러', value: 'Samsung Pascal' },
    { label: 'DRAM', value: '2GB LPDDR4' },
    { label: 'TBW', value: '1,200 TB' },
    { label: 'MTBF', value: '1,500,000 시간' },
    { label: '전력 소비 (평균)', value: '5.8W' },
    { label: '크기', value: '80.15 x 22.15 x 2.38 mm' },
    { label: '무게', value: '약 9g' },
    { label: '보증 기간', value: '5년 또는 TBW 중 먼저 도래하는 조건' },
    { label: '지원 기능', value: 'AES 256-bit 암호화, TCG/Opal V2.0, TRIM, S.M.A.R.T' },
  ];

  const reviews = [
    {
      id: 1,
      name: '김**',
      rating: 5,
      date: '2025.01.10',
      content:
        '980 PRO에서 업그레이드했는데 체감 속도가 확실히 빨라졌습니다. 대용량 파일 복사 시 속도가 일정하게 유지되는 점이 좋습니다. 발열도 이전 모델보다 개선된 느낌입니다.',
      helpful: 24,
    },
    {
      id: 2,
      name: '이**',
      rating: 5,
      date: '2025.01.08',
      content:
        '게임 로딩 속도가 눈에 띄게 빨라졌습니다. PS5에서도 호환 잘 되고, 2TB라 용량 걱정 없이 사용하고 있습니다. 삼성 정품이라 안심이 됩니다.',
      helpful: 18,
    },
    {
      id: 3,
      name: '박**',
      rating: 5,
      date: '2025.01.05',
      content:
        '영상 편집 작업용으로 구매했습니다. 4K 영상 편집 시 프리뷰가 끊김 없이 재생되고, 렌더링 속도도 크게 향상되었습니다. 가격 대비 최고의 선택입니다.',
      helpful: 15,
    },
    {
      id: 4,
      name: '최**',
      rating: 4,
      date: '2024.12.28',
      content:
        '전반적으로 매우 만족합니다. 벤치마크 수치도 스펙에 근접하게 나오고, 실사용에서도 빠릿빠릿합니다. 다만 방열판이 별도 구매해야 하는 점은 아쉽습니다.',
      helpful: 12,
    },
    {
      id: 5,
      name: '정**',
      rating: 5,
      date: '2024.12.25',
      content:
        '회사에서 대량 구매했는데 배송도 빠르고 정품 확인도 깔끔하게 되었습니다. 직원들 워크스테이션 업그레이드에 딱 좋은 제품입니다.',
      helpful: 9,
    },
  ];

  const tabs = [
    { key: 'desc' as const, label: '상품 상세' },
    { key: 'spec' as const, label: '제품 사양' },
    { key: 'review' as const, label: `구매 후기 (${reviews.length})` },
  ];

  return (
    <div className="mt-16">
      {/* Tab Navigation */}
      <div className="flex border-b border-gray-200">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex-1 py-4 text-sm font-semibold text-center transition-colors cursor-pointer whitespace-nowrap ${
              activeTab === tab.key
                ? 'text-teal-600 border-b-2 border-teal-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="py-10">
        {/* Description Tab */}
        {activeTab === 'desc' && (
          <div className="space-y-10">
            {/* Product Highlight */}
            <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-8 md:p-12 text-white">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                <div>
                  <h3 className="text-2xl md:text-3xl font-bold mb-4">Samsung 990 PRO</h3>
                  <p className="text-lg text-gray-300 mb-2">차세대 성능의 기준</p>
                  <p className="text-sm text-gray-400 leading-relaxed mb-6">
                    삼성전자의 최신 V-NAND 기술과 독자 개발 Pascal 컨트롤러가 만나 전작 대비 한 단계
                    진화한 성능을 제공합니다. 최대 7,450MB/s의 순차 읽기 속도와 6,900MB/s의 순차 쓰기
                    속도로 대용량 데이터 처리, 고사양 게임, 전문 영상 편집 등 모든 작업에서 탁월한 성능을
                    발휘합니다.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <span className="bg-white/10 px-4 py-2 rounded-full text-xs font-medium">PCIe 4.0</span>
                    <span className="bg-white/10 px-4 py-2 rounded-full text-xs font-medium">NVMe 2.0</span>
                    <span className="bg-white/10 px-4 py-2 rounded-full text-xs font-medium">V-NAND</span>
                    <span className="bg-white/10 px-4 py-2 rounded-full text-xs font-medium">5년 보증</span>
                  </div>
                </div>
                <div className="w-full h-64 rounded-xl overflow-hidden">
                  <img
                    src="https://public.readdy.ai/ai/img_res/23e58a370dc6cf06316b3b39bfebf378.jpg"
                    alt="삼성 990 PRO"
                    title="삼성전자 990 PRO 2TB NVMe SSD"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>

            {/* Features */}
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-6">주요 특징</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-gray-50 rounded-xl p-6">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-flashlight-line text-teal-600 text-2xl"></i>
                  </div>
                  <h4 className="text-base font-bold text-gray-900 mb-2">극한의 속도</h4>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    PCIe 4.0 인터페이스 기반 최대 7,450MB/s 읽기 속도로 대용량 파일도 순식간에 처리합니다.
                    랜덤 성능도 최대 1,550K IOPS로 멀티태스킹 환경에서도 빠릿한 반응 속도를 제공합니다.
                  </p>
                </div>
                <div className="bg-gray-50 rounded-xl p-6">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-temp-cold-line text-teal-600 text-2xl"></i>
                  </div>
                  <h4 className="text-base font-bold text-gray-900 mb-2">향상된 열 관리</h4>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    니켈 코팅 컨트롤러와 열 분산 라벨이 적용되어 장시간 고부하 작업에서도 안정적인 성능을
                    유지합니다. Dynamic Thermal Guard 기술로 과열을 방지합니다.
                  </p>
                </div>
                <div className="bg-gray-50 rounded-xl p-6">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-shield-star-line text-teal-600 text-2xl"></i>
                  </div>
                  <h4 className="text-base font-bold text-gray-900 mb-2">높은 내구성</h4>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    1,200TBW의 높은 내구성으로 장기간 안심하고 사용할 수 있습니다. AES 256-bit 하드웨어
                    암호화로 데이터 보안도 철저하게 보호합니다.
                  </p>
                </div>
              </div>
            </div>

            {/* Use Cases */}
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-6">이런 분께 추천합니다</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex items-start gap-4 bg-white border border-gray-200 rounded-xl p-5">
                  <div className="w-10 h-10 bg-teal-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-gamepad-line text-white text-lg"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-gray-900 mb-1">고사양 게이머</h4>
                    <p className="text-sm text-gray-600">
                      게임 로딩 시간을 획기적으로 단축하고, PS5와도 완벽 호환됩니다.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4 bg-white border border-gray-200 rounded-xl p-5">
                  <div className="w-10 h-10 bg-teal-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-film-line text-white text-lg"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-gray-900 mb-1">영상 크리에이터</h4>
                    <p className="text-sm text-gray-600">
                      4K/8K 영상 편집 시 끊김 없는 프리뷰와 빠른 렌더링을 경험하세요.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4 bg-white border border-gray-200 rounded-xl p-5">
                  <div className="w-10 h-10 bg-teal-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-code-s-slash-line text-white text-lg"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-gray-900 mb-1">개발자 / 엔지니어</h4>
                    <p className="text-sm text-gray-600">
                      대규모 프로젝트 빌드와 가상 머신 운영에 최적의 I/O 성능을 제공합니다.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4 bg-white border border-gray-200 rounded-xl p-5">
                  <div className="w-10 h-10 bg-teal-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-building-line text-white text-lg"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-gray-900 mb-1">기업 / 워크스테이션</h4>
                    <p className="text-sm text-gray-600">
                      대량 데이터 처리와 안정적인 운영이 필요한 비즈니스 환경에 적합합니다.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Spec Tab */}
        {activeTab === 'spec' && (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <table className="w-full">
              <tbody>
                {specs.map((spec, index) => (
                  <tr
                    key={index}
                    className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}
                  >
                    <td className="px-6 py-4 text-sm font-medium text-gray-700 w-1/3 border-r border-gray-200">
                      {spec.label}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">{spec.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Review Tab */}
        {activeTab === 'review' && (
          <div className="space-y-6">
            {/* Rating Summary */}
            <div className="bg-gray-50 rounded-xl p-6 flex items-center gap-8">
              <div className="text-center">
                <div className="text-4xl font-bold text-gray-900 mb-1">4.9</div>
                <div className="flex items-center justify-center mb-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <i
                      key={star}
                      className="ri-star-fill text-yellow-400 text-lg"
                    ></i>
                  ))}
                </div>
                <p className="text-xs text-gray-500">{reviews.length}개 리뷰</p>
              </div>
              <div className="flex-1 space-y-2">
                {[5, 4, 3, 2, 1].map((rating) => {
                  const count = reviews.filter((r) => r.rating === rating).length;
                  const percentage = (count / reviews.length) * 100;
                  return (
                    <div key={rating} className="flex items-center gap-3">
                      <span className="text-xs text-gray-600 w-8">{rating}점</span>
                      <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-yellow-400 rounded-full transition-all"
                          style={{ width: `${percentage}%` }}
                        ></div>
                      </div>
                      <span className="text-xs text-gray-500 w-8">{count}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Review List */}
            {reviews.map((review) => (
              <div
                key={review.id}
                className="bg-white border border-gray-200 rounded-xl p-6"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-teal-100 rounded-full flex items-center justify-center">
                      <i className="ri-user-line text-teal-600"></i>
                    </div>
                    <div>
                      <span className="text-sm font-semibold text-gray-900">
                        {review.name}
                      </span>
                      <span className="text-xs text-gray-400 ml-2">{review.date}</span>
                    </div>
                  </div>
                  <div className="flex items-center">
                    {[...Array(review.rating)].map((_, i) => (
                      <i
                        key={i}
                        className="ri-star-fill text-yellow-400 text-sm"
                      ></i>
                    ))}
                    {[...Array(5 - review.rating)].map((_, i) => (
                      <i
                        key={i}
                        className="ri-star-line text-gray-300 text-sm"
                      ></i>
                    ))}
                  </div>
                </div>
                <p className="text-sm text-gray-700 leading-relaxed mb-3">
                  {review.content}
                </p>
                <div className="flex items-center gap-1 text-xs text-gray-400">
                  <i className="ri-thumb-up-line"></i>
                  <span>도움이 됐어요 {review.helpful}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
